import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Compass, Calculator, FileCheck2 } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const SITE_URL = 'https://mundalo.com';

/**
 * /tools - hub page linking to the three free SEO/conversion tools.
 *
 * Card 1 teal background - Move Score.
 * Card 2 cream with navy border - Calculator.
 * Card 3 navy background - Visa Finder.
 */
const TOOLS = [
  {
    id: 'readiness',
    name: 'Mexico Readiness Score',
    desc: 'Six questions, instant personalized score out of 100 - find out exactly where you stand and what you need to move.',
    to: '/mexico-readiness-score',
    icon: Compass,
    bg: 'bg-[#2CA8A0]',
    text: 'text-[#F9F6EF]',
    sub: 'text-[#F9F6EF]/85',
    accentText: 'text-[#F9F6EF]/65',
    cta: 'bg-[#E8A830] text-[#0A1F2E]',
  },
  {
    id: 'cost',
    name: 'Cost of Living Calculator',
    desc: 'Pick a city, pick a lifestyle, get the real number - across 11 Mexican cities and three lifestyle levels.',
    to: '/mexico-cost-of-living-calculator',
    icon: Calculator,
    bg: 'bg-[#F9F6EF]',
    text: 'text-[#0A1F2E]',
    sub: 'text-[#0A1F2E]/75',
    accentText: 'text-[#2CA8A0]',
    cta: 'bg-[#0A1F2E] text-[#F9F6EF]',
    border: 'border-2 border-[#0A1F2E]',
  },
  {
    id: 'visa',
    name: 'Mexico Visa Finder',
    desc: 'Four questions, your exact visa - verified against 2026 INM requirements and the seven main pathways for Americans.',
    to: '/mexico-visa-finder',
    icon: FileCheck2,
    bg: 'bg-[#0A1F2E]',
    text: 'text-[#F9F6EF]',
    sub: 'text-[#F9F6EF]/85',
    accentText: 'text-[#2CA8A0]',
    cta: 'bg-[#E8A830] text-[#0A1F2E]',
  },
];

const ToolsHub = () => (
  <div className="min-h-screen bg-[#F9F6EF] flex flex-col">
    <Helmet>
      <title>Free Mexico Relocation Tools | Mundalo</title>
      <meta name="description" content="Three free tools to plan your move to Mexico. Readiness Score, Cost of Living Calculator, and Visa Finder. Built by expats, for expats." />
      <link rel="canonical" href={`${SITE_URL}/tools`} />
      <meta property="og:title" content="Free Mexico Relocation Tools | Mundalo" />
      <meta property="og:description" content="Readiness Score, Cost of Living Calculator, Visa Finder. Built by expats, for expats." />
      <meta property="og:url" content={`${SITE_URL}/tools`} />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary_large_image" />
    </Helmet>

    <Header />

    <main className="flex-1">
      <section className="pt-16 sm:pt-24 pb-12 px-6 md:px-10">
        <div className="max-w-3xl mx-auto text-center">
          <p className="font-mono uppercase text-[11px] tracking-[0.2em] text-[#2CA8A0] mb-4">
            Free Mundalo tools
          </p>
          <h1 className="font-display italic text-5xl sm:text-6xl md:text-7xl text-[#0A1F2E] leading-[1.04] mb-6">
            Free Tools for Your Mexico Move
          </h1>
          <p className="text-base sm:text-lg text-[#0A1F2E]/75 leading-relaxed">
            Built by people who made the move. Free forever.
          </p>
        </div>
      </section>

      <section className="px-6 md:px-10 pb-24">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
          {TOOLS.map((t) => {
            const Icon = t.icon;
            return (
              <Link
                key={t.id}
                to={t.to}
                data-testid={`tool-card-${t.id}`}
                className={`group rounded-2xl ${t.bg} ${t.text} ${t.border || ''} p-7 sm:p-8 flex flex-col transition hover:shadow-lg`}
              >
                <Icon className="w-9 h-9 mb-5 opacity-90" strokeWidth={1.4} />
                <p className={`font-mono uppercase text-[10px] tracking-[0.2em] mb-2 ${t.accentText}`}>
                  Free tool
                </p>
                <h2 className={`font-display italic text-3xl sm:text-4xl leading-tight mb-4 ${t.text}`}>
                  {t.name}
                </h2>
                <p className={`text-sm sm:text-base leading-relaxed mb-7 flex-1 ${t.sub}`}>
                  {t.desc}
                </p>
                <span
                  className={`inline-flex items-center justify-center gap-2 px-5 py-3 ${t.cta} font-mono uppercase text-xs tracking-[0.14em] rounded-full font-semibold w-fit group-hover:translate-x-1 transition`}
                >
                  Open tool
                  <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
            );
          })}
        </div>
      </section>
    </main>

    <Footer />
  </div>
);

export default ToolsHub;
